"use client";

import {
  createInitialPackage,
  createNewPackage,
  createNewShipmentItem,
  getPackageAggregates,
  itemWeightToKg,
  type Location,
  type PackageItem,
  type PackagePreset,
  type ShipmentItem,
} from "@/app/encomendas/types";
import {
  fetchQuickQuote,
  type CotacaoPackage,
  type ShippingOption,
} from "@/components/Molecules/CotacaoDialog";
import { writeBasePackages } from "@/lib/encomendas/packageStorage";
import { incrementDropoffRanking } from "@/utils/dropoffRankingUtils";
import { showNeutralToast } from "@/utils/toastUtils";
import { useEffect, useRef, useState } from "react";

// Exportados para que quem preenche o formulário por outro caminho (ex.: o
// parâmetro `box` das LPs de rota) aplique EXATAMENTE os mesmos limites da
// digitação manual, em vez de manter uma cópia que pode divergir.
export const DIM_MAXES = { lengthCm: 39, widthCm: 28.5, heightCm: 18 } as const;
const DIM_MESSAGES = {
  lengthCm: "Comprimento até 39 cm",
  widthCm: "Largura até 28,5 cm",
  heightCm: "Altura até 18 cm",
} as const;

// Campo apagado cobra o preenchimento na hora, como o peso do item já faz.
const DIM_REQUIRED_MESSAGES = {
  lengthCm: "Informe o comprimento do pacote",
  widthCm: "Informe a largura do pacote",
  heightCm: "Informe a altura do pacote",
} as const;

export const MAX_WEIGHT_KG = 30;

// Uma regra só para a digitação e para o envio da cotação — com duas cópias,
// o campo poderia ficar sem erro em tela e ainda barrar a cotação.
const dimensionError = (
  field: keyof typeof DIM_MAXES,
  rawValue: string
): string => {
  const raw = rawValue.trim();
  const n = parseFloat(raw.replace(",", "."));
  if (!raw || isNaN(n) || n <= 0) return DIM_REQUIRED_MESSAGES[field];
  if (n > DIM_MAXES[field]) return DIM_MESSAGES[field];
  return "";
};

// O quick-quote responde 502 (`provider_error`) quando a transportadora recusa
// o payload — entre outros casos, com peso fracionado em pacote recém-criado.
const isBadGatewayError = (err: unknown) =>
  (err as { response?: { status?: number } })?.response?.status === 502;

function fmt1(val: number) {
  return val % 1 === 0 ? String(val) : val.toFixed(1);
}

interface UsePackageHandlersInput {
  packages: PackageItem[];
  setPackages: React.Dispatch<React.SetStateAction<PackageItem[]>>;
  packagePresets: PackagePreset[];
  inputValues: Record<string, string>;
  setInputValues: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  selectedOrigin: Location | null;
  selectedDestination: Location | null;
  origin: string;
  destination: string;
}

export function usePackageHandlers({
  packages,
  setPackages,
  packagePresets,
  inputValues,
  setInputValues,
  selectedOrigin,
  selectedDestination,
  origin,
  destination,
}: UsePackageHandlersInput) {
  const [inputErrors, setInputErrors] = useState<Record<string, string>>({});
  const [selectedItemIndex, setSelectedItemIndex] = useState<
    Record<string, number>
  >({});
  const [cotacaoOpen, setCotacaoOpen] = useState(false);
  const [cotacaoLoading, setCotacaoLoading] = useState(false);
  const [cotacaoError, setCotacaoError] = useState("");
  const [cotacaoOptions, setCotacaoOptions] = useState<ShippingOption[]>([]);
  const [cotacaoPackages, setCotacaoPackages] = useState<CotacaoPackage[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [cachedQuoteKey, setCachedQuoteKey] = useState<string | null>(null);
  const [quoteRetryExhausted, setQuoteRetryExhausted] = useState(false);
  const [quoteRetryNonce, setQuoteRetryNonce] = useState(0);
  const autoQuoteRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightQuoteKeyRef = useRef<string | null>(null);

  const getInputValue = (pkgId: string, field: string) =>
    inputValues[`${pkgId}_${field}`] ?? "";

  const setInputValue = (pkgId: string, field: string, value: string) =>
    setInputValues((p) => ({ ...p, [`${pkgId}_${field}`]: value }));

  const getInputError = (pkgId: string, field: string) =>
    inputErrors[`${pkgId}_${field}`] || "";

  const getItemInputValue = (pkgId: string, itemId: string, field: string) => {
    const key = `${pkgId}_item_${itemId}_${field}`;
    return key in inputValues ? inputValues[key] : undefined;
  };

  const getItemInputError = (pkgId: string, itemId: string, field: string) =>
    inputErrors[`${pkgId}_item_${itemId}_${field}`] || "";

  const handlePackageFieldChange = (
    pkgId: string,
    field: "lengthCm" | "widthCm" | "heightCm",
    rawValue: string
  ) => {
    setIsDirty(true);
    const maxVal = DIM_MAXES[field];
    const num = Math.min(
      Math.max(0, parseFloat(rawValue.replace(",", ".")) || 0),
      maxVal
    );
    const display =
      rawValue.trim() && parseFloat(rawValue.replace(",", ".")) > maxVal
        ? fmt1(maxVal)
        : rawValue;
    setPackages((p) =>
      p.map((pkg) => (pkg.id === pkgId ? { ...pkg, [field]: num } : pkg))
    );
    setInputValue(pkgId, field, display);
    setInputErrors((p) => {
      const n = { ...p };
      const key = `${pkgId}_${field}`;
      const message = dimensionError(field, display);
      if (message) n[key] = message;
      else delete n[key];
      return n;
    });
  };

  const handlePresetChange = (pkgId: string, presetId: string) => {
    setIsDirty(true);
    const preset = packagePresets.find((p) => p.id.toString() === presetId);
    let updatedPkg: PackageItem | undefined;
    setPackages((prev) =>
      prev.map((p) => {
        if (p.id !== pkgId) return p;
        if (!preset) {
          const initial = createInitialPackage();
          const items = (p.items || []).map((item, idx) =>
            idx === 0
              ? {
                  ...item,
                  weightValue: initial.items![0].weightValue,
                  weightUnit: initial.items![0].weightUnit,
                  unitValueBRL: initial.items![0].unitValueBRL,
                }
              : item
          );
          const next = {
            ...p,
            widthCm: initial.widthCm,
            heightCm: initial.heightCm,
            lengthCm: initial.lengthCm,
            selectedPresetId: "",
            items,
          };
          setInputValue(pkgId, "widthCm", fmt1(next.widthCm));
          setInputValue(pkgId, "heightCm", fmt1(next.heightCm));
          setInputValue(pkgId, "lengthCm", fmt1(next.lengthCm));
          const firstItem = items[0];
          if (firstItem) {
            setInputValues((v) => ({
              ...v,
              [`${pkgId}_item_${firstItem.id}_weightValue`]: String(
                initial.items![0].weightValue
              ),
              [`${pkgId}_item_${firstItem.id}_unitValueBRL`]: String(
                initial.items![0].unitValueBRL
              ),
            }));
          }
          updatedPkg = next;
          return next;
        }
        // Presets are stored in meters/kg/BRL (same convention as the quote
        // payload — see CotacaoDialog), so dims need the inverse ×100 back to cm.
        const widthCm = Math.min(
          parseFloat(preset.width) * 100,
          DIM_MAXES.widthCm
        );
        const heightCm = Math.min(
          parseFloat(preset.height) * 100,
          DIM_MAXES.heightCm
        );
        const lengthCm = Math.min(
          parseFloat(preset.length) * 100,
          DIM_MAXES.lengthCm
        );
        const weightValue = parseFloat(preset.weight);
        const unitValueBRL = parseFloat(preset.unit_value) || 0;
        const items = (p.items || []).map((item, idx) =>
          idx === 0
            ? {
                ...item,
                weightValue: isNaN(weightValue) ? undefined : weightValue,
                weightUnit: "kg" as const,
                unitValueBRL,
              }
            : item
        );
        const next = {
          ...p,
          widthCm,
          heightCm,
          lengthCm,
          selectedPresetId: presetId,
          items,
        };
        setInputValue(pkgId, "widthCm", fmt1(next.widthCm));
        setInputValue(pkgId, "heightCm", fmt1(next.heightCm));
        setInputValue(pkgId, "lengthCm", fmt1(next.lengthCm));
        const firstItem = items[0];
        if (firstItem) {
          setInputValues((v) => ({
            ...v,
            [`${pkgId}_item_${firstItem.id}_weightValue`]: isNaN(weightValue)
              ? ""
              : String(weightValue),
            [`${pkgId}_item_${firstItem.id}_unitValueBRL`]:
              String(unitValueBRL),
          }));
        }
        updatedPkg = next;
        return next;
      })
    );
    if (updatedPkg) {
      const pkg = updatedPkg;
      setInputErrors((prev) => validateWeights(pkg, prev));
    }
  };

  const validateWeights = (pkg: PackageItem, errs: Record<string, string>) => {
    const next = { ...errs };
    const totalKey = `${pkg.id}_totalWeight`;
    delete next[totalKey];
    if (getPackageAggregates(pkg).weightKg > MAX_WEIGHT_KG)
      next[totalKey] = "O peso de um mais pacotes não pode ultrapassar 30 kg";
    (pkg.items || []).forEach((item) => {
      const k = `${pkg.id}_item_${item.id}_weightVolume`;
      delete next[k];
      const w = itemWeightToKg(item);
      if (w <= 0) next[k] = "Informe o peso/volume do item";
      else if (w > MAX_WEIGHT_KG) next[k] = "Peso máximo por item é 30 kg";
    });
    return next;
  };

  const handleItemFieldChange = (
    pkgId: string,
    itemId: string,
    field: keyof ShipmentItem,
    rawValue: string
  ) => {
    setIsDirty(true);
    const key = `${pkgId}_item_${itemId}_${field}`;
    setInputValues((p) => ({ ...p, [key]: rawValue }));

    const newPkgs = packages.map((p) => {
      if (p.id !== pkgId) return p;
      return {
        ...p,
        items: (p.items || []).map((i) => {
          if (i.id !== itemId) return i;
          if (field === "name" || field === "weightVolume")
            return { ...i, [field]: rawValue };
          if (field === "weightValue") {
            const num = parseFloat(rawValue.replace(",", "."));
            return {
              ...i,
              weightValue: isNaN(num) || num < 0 ? undefined : num,
            };
          }
          if (field === "weightUnit")
            return {
              ...i,
              weightUnit: rawValue === "ml" ? ("ml" as const) : ("kg" as const),
            };
          const num = parseFloat(rawValue.replace(",", ".")) || 0;
          const clamped =
            field === "quantity"
              ? Math.min(Math.max(1, Math.round(num)), 99)
              : Math.min(num, 999999);
          return { ...i, [field]: clamped };
        }),
      };
    });
    setPackages(newPkgs);

    const updated = newPkgs.find((p) => p.id === pkgId);
    if (!updated) return;
    setInputErrors((prev) => {
      let next = { ...prev };
      if (field === "unitValueBRL") {
        const n = parseFloat(rawValue.replace(",", ".")) || 0;
        if (rawValue.trim() && n <= 0)
          next[key] = "Valor declarado deve ser maior que R$ 0";
        else delete next[key];
      }
      return validateWeights(updated, next);
    });
  };

  const addItem = (pkgId: string) => {
    const newItem = createNewShipmentItem();
    let newIdx = 0;
    setPackages((prev) => {
      const pkg = prev.find((p) => p.id === pkgId);
      const items = [...(pkg?.items || []), newItem];
      newIdx = items.length - 1;
      return prev.map((p) => (p.id === pkgId ? { ...p, items } : p));
    });
    setSelectedItemIndex((p) => ({ ...p, [pkgId]: newIdx }));
  };

  const removeItem = (pkgId: string, itemId: string) => {
    setPackages((prev) =>
      prev.map((p) => {
        if (p.id !== pkgId) return p;
        const items = (p.items || []).filter((i) => i.id !== itemId);
        return {
          ...p,
          items: items.length > 0 ? items : [createNewShipmentItem()],
        };
      })
    );
    setSelectedItemIndex((prev) => {
      const pkg = packages.find((p) => p.id === pkgId);
      const items = (pkg?.items || []).filter((i) => i.id !== itemId);
      return {
        ...prev,
        [pkgId]: Math.min(prev[pkgId] ?? 0, Math.max(0, items.length - 1)),
      };
    });
  };

  // Trava o botão de cotação assim que a lista de pacotes muda, antes mesmo do
  // debounce disparar, para não deixar o usuário seguir com um preço velho.
  const markQuoteStale = () => setIsDirty(true);

  const addPackage = () => {
    markQuoteStale();
    setPackages((p) => [...p, createNewPackage()]);
  };

  const isFormReady = (() => {
    if (!selectedOrigin || !selectedDestination) return false;
    if (selectedOrigin.id === selectedDestination.id) return false;
    if (
      selectedOrigin.ibge_id != null &&
      selectedDestination.ibge_id != null &&
      selectedOrigin.ibge_id === selectedDestination.ibge_id
    )
      return false;
    for (const pkg of packages) {
      for (const field of ["lengthCm", "widthCm", "heightCm"] as const) {
        const n = parseFloat(
          (inputValues[`${pkg.id}_${field}`] || "").replace(",", ".")
        );
        if (!n || n <= 0) return false;
      }
      const items = pkg.items || [];
      if (!items.length) return false;
      for (const item of items) {
        const raw =
          inputValues[`${pkg.id}_item_${item.id}_unitValueBRL`] ??
          (item.unitValueBRL ? String(item.unitValueBRL) : "");
        const n = parseFloat((raw || "").replace(",", "."));
        if (!raw || isNaN(n) || n <= 0) return false;
        if (itemWeightToKg(item) <= 0) return false;
      }
      if (getPackageAggregates(pkg).weightKg > MAX_WEIGHT_KG) return false;
    }
    return true;
  })();

  // Zera a parte decimal dos pesos acima de 1 kg (25,2 -> 25) e devolve se
  // mexeu em algo. Pesos abaixo de 1 kg ficam como estão, senão virariam zero,
  // e só mexe em kg — em ml o valor digitado já é inteiro.
  const dropFractionalWeights = () => {
    const changedInputs: Record<string, string> = {};
    const nextPackages = packages.map((pkg) => ({
      ...pkg,
      items: (pkg.items || []).map((item) => {
        const value = item.weightValue;
        if ((item.weightUnit ?? "kg") !== "kg" || value == null) return item;
        if (value <= 1 || Number.isInteger(value)) return item;
        const floored = Math.floor(value);
        changedInputs[`${pkg.id}_item_${item.id}_weightValue`] =
          String(floored);
        return { ...item, weightValue: floored };
      }),
    }));

    if (Object.keys(changedInputs).length === 0) return false;
    setInputValues((prev) => ({ ...prev, ...changedInputs }));
    setPackages(nextPackages);
    showNeutralToast(
      "Ocorreu um erro com o valor de peso indicado e o valor foi arredondado."
    );
    return true;
  };

  // Auto-trigger a silent quote whenever isFormReady flips true or inputs settle
  useEffect(() => {
    if (!isFormReady) return;
    const oIbge = selectedOrigin?.ibge_id ?? null;
    const dIbge = selectedDestination?.ibge_id ?? null;
    if (!oIbge || !dIbge) return;

    const pkgsForApi = packages.map((p) => {
      const agg = getPackageAggregates(p);
      return {
        ...p,
        weightKg: agg.weightKg,
        unitValueBRL: agg.unitValueBRL,
        quantity: agg.quantity,
      };
    });
    // Só o que muda preço entra na chave — renomear um pacote ou trocar o
    // preset não pode disparar uma cotação nova.
    const quoteKey = JSON.stringify({
      oIbge,
      dIbge,
      pkgs: pkgsForApi.map((p) => [
        p.lengthCm,
        p.widthCm,
        p.heightCm,
        p.weightKg,
        p.unitValueBRL,
        p.quantity,
      ]),
    });
    // Voltou para um estado já cotado (ex.: adicionou e removeu o pacote): a
    // cotação em tela vale de novo, então libera o botão em vez de travar.
    if (quoteKey === cachedQuoteKey) {
      setIsDirty(false);
      return;
    }

    if (autoQuoteRef.current) clearTimeout(autoQuoteRef.current);
    autoQuoteRef.current = setTimeout(async () => {
      inFlightQuoteKeyRef.current = quoteKey;
      const isStale = () => inFlightQuoteKeyRef.current !== quoteKey;
      setQuoteRetryExhausted(false);
      setCotacaoLoading(true);
      // Persist origin, destination and packages so pacotes/page can read them
      // on navigation — matches what calculateFrete does for the manual path.
      try {
        if (typeof window !== "undefined") {
          localStorage.setItem(
            "embarca-log.origin",
            JSON.stringify(selectedOrigin ?? { label: origin })
          );
          localStorage.setItem(
            "embarca-log.destination",
            JSON.stringify(selectedDestination ?? { label: destination })
          );
          writeBasePackages(pkgsForApi);
        }
      } catch {}
      try {
        const opts = await fetchQuickQuote({
          originIbge: oIbge,
          destinationIbge: dIbge,
          packages: pkgsForApi,
        });
        if (isStale()) return;
        setCotacaoOptions(opts);
        setCotacaoPackages(pkgsForApi);
        setCachedQuoteKey(quoteKey);
        setIsDirty(false);
        setCotacaoError("");
        setCotacaoLoading(false);
      } catch (err) {
        if (isStale()) return;
        // O provedor derruba a cotação (502) com peso fracionado. Arredondar
        // para baixo altera `packages`, o efeito roda de novo e refaz a
        // cotação; como o peso já fica inteiro, não há como repetir em loop.
        if (isBadGatewayError(err) && dropFractionalWeights()) return;
        setCotacaoError("Falha ao buscar cotações.");
        setQuoteRetryExhausted(true);
        setCotacaoLoading(false);
      }
    }, 800);

    return () => {
      if (autoQuoteRef.current) clearTimeout(autoQuoteRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isFormReady,
    selectedOrigin?.ibge_id,
    selectedDestination?.ibge_id,
    packages,
    inputValues,
    quoteRetryNonce,
  ]);

  const retryQuote = () => {
    setCotacaoError("");
    setQuoteRetryExhausted(false);
    setQuoteRetryNonce((n) => n + 1);
  };

  const calculateFrete = (
    setOriginError: (e: string) => void,
    setDestinationError: (e: string) => void,
    setShowErrors: (v: boolean) => void
  ) => {
    let hasError = false;
    const errs: Record<string, string> = {};

    if (!selectedOrigin) {
      hasError = true;
      setOriginError("Selecione um local de envio válido");
    } else {
      setOriginError("");
    }

    if (!selectedDestination) {
      hasError = true;
      setDestinationError("Selecione um local de retirada válido");
    } else if (selectedOrigin?.id === selectedDestination.id) {
      hasError = true;
      setDestinationError(
        "O local de retirada deve ser diferente do local de envio"
      );
    } else if (
      selectedOrigin?.ibge_id != null &&
      selectedDestination.ibge_id != null &&
      selectedDestination.ibge_id === selectedOrigin.ibge_id
    ) {
      hasError = true;
      setDestinationError("O local de retirada deve ser em outra cidade");
    } else {
      setDestinationError("");
    }

    packages.forEach((pkg) => {
      if (getPackageAggregates(pkg).weightKg > MAX_WEIGHT_KG) {
        hasError = true;
        errs[`${pkg.id}_totalWeight`] =
          "O peso de um mais pacotes não pode ultrapassar 30 kg";
      }
      (["lengthCm", "widthCm", "heightCm"] as const).forEach((field) => {
        const message = dimensionError(
          field,
          inputValues[`${pkg.id}_${field}`] || ""
        );
        if (message) {
          hasError = true;
          errs[`${pkg.id}_${field}`] = message;
        }
      });
      (pkg.items || []).forEach((item) => {
        const valKey = `${pkg.id}_item_${item.id}_unitValueBRL`;
        const raw =
          inputValues[valKey] ??
          (item.unitValueBRL ? String(item.unitValueBRL) : "");
        const n = parseFloat((raw || "").replace(",", "."));
        if (!raw || isNaN(n) || n <= 0) {
          hasError = true;
          errs[valKey] = "Valor declarado deve ser maior que R$ 0";
        }
        const w = itemWeightToKg(item);
        if (w <= 0) {
          hasError = true;
          errs[`${pkg.id}_item_${item.id}_weightVolume`] =
            "Informe o peso/volume do item";
        } else if (w > MAX_WEIGHT_KG) {
          hasError = true;
          errs[`${pkg.id}_item_${item.id}_weightVolume`] =
            "Peso máximo por item é 30 kg";
        }
      });
    });

    setInputErrors(errs);
    setShowErrors(true);
    if (hasError) return;

    const pkgsForApi = packages.map((p) => {
      const agg = getPackageAggregates(p);
      return {
        ...p,
        weightKg: agg.weightKg,
        unitValueBRL: agg.unitValueBRL,
        quantity: agg.quantity,
      };
    });

    try {
      if (typeof window !== "undefined") {
        localStorage.setItem(
          "embarca-log.origin",
          JSON.stringify(selectedOrigin ?? { label: origin })
        );
        localStorage.setItem(
          "embarca-log.destination",
          JSON.stringify(selectedDestination ?? { label: destination })
        );
        writeBasePackages(pkgsForApi);
        if (selectedOrigin?.id != null)
          incrementDropoffRanking("origin", selectedOrigin.id);
        if (selectedDestination?.id != null)
          incrementDropoffRanking("destination", selectedDestination.id);
      }
    } catch {}

    const oIbge = selectedOrigin?.ibge_id ?? null;
    const dIbge = selectedDestination?.ibge_id ?? null;
    if (!oIbge || !dIbge) {
      setCotacaoError("Não foi possível identificar as localidades.");
      setCotacaoOpen(true);
      return;
    }

    setCotacaoPackages(pkgsForApi);
    setCotacaoError("");
    setCotacaoOpen(true);
  };

  return {
    inputErrors,
    setInputErrors,
    selectedItemIndex,
    setSelectedItemIndex,
    cotacaoOpen,
    setCotacaoOpen,
    cotacaoLoading,
    cotacaoError,
    setCotacaoError,
    cotacaoOptions,
    setCotacaoOptions,
    cotacaoPackages,
    getInputValue,
    setInputValue,
    getInputError,
    getItemInputValue,
    getItemInputError,
    handlePackageFieldChange,
    handlePresetChange,
    handleItemFieldChange,
    addItem,
    removeItem,
    addPackage,
    isFormReady,
    calculateFrete,
    isDirty,
    markQuoteStale,
    quoteRetryExhausted,
    retryQuote,
  };
}
